import { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { LayoutProfile } from '../lib/types/sidebar';
import { layoutProfileService, defaultLayoutProfile } from '../services/layoutProfileService';

export const useLayoutProfile = () => {
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState<LayoutProfile>(defaultLayoutProfile);
  const [allProfiles, setAllProfiles] = useState<LayoutProfile[]>([defaultLayoutProfile]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Memoize roles as a string to prevent unnecessary re-renders
  const rolesKey = useMemo(() => {
    return currentUser?.roles?.join(',') || '';
  }, [currentUser?.roles]);
  
  // Store current roles in a ref for use in callback
  const rolesRef = useRef(currentUser?.roles);
  rolesRef.current = currentUser?.roles;

  useEffect(() => {
    let isMounted = true;
    
    // Subscribe to profile changes
    const unsubscribe = layoutProfileService.subscribeToProfiles((profiles) => {
      if (!isMounted) return;
      
      setAllProfiles(profiles);
      
      const currentRoles = rolesRef.current;
      if (currentRoles && currentRoles.length > 0) {
        const matchedProfile = layoutProfileService.matchProfileToRoles(
          profiles,
          currentRoles
        );
        setProfile(matchedProfile);
      } else {
        const defaultProfile = profiles.find(p => p.isDefault) || defaultLayoutProfile;
        setProfile(defaultProfile);
      }
      
      setIsLoading(false);
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []); // Empty dependency - only subscribe once

  // Update profile when roles change
  useEffect(() => {
    if (allProfiles.length > 0 && currentUser?.roles) {
      const matchedProfile = layoutProfileService.matchProfileToRoles(
        allProfiles,
        currentUser.roles
      );
      setProfile(matchedProfile);
    }
  }, [rolesKey]); // Only re-run when roles string changes

  return {
    profile,
    allProfiles,
    isLoading,
    isSidebar: profile.layoutType === 'sidebar',
    isSimpleList: profile.layoutType === 'simple-list',
  };
};

